import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextInput,
  Select,
  MultiSelect,
  Checkbox,
  Button,
  Text,
  Space,
  ScrollArea,
  Loader,
  Card,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { Timestamp } from "firebase/firestore";
import { FieldValueDisplay } from "./submission-field-display";
import { FlexFormTemplate, TemplateSubmission, FlexOptionsSet, FormFieldConfig } from "@captiva/shared-types";

// Editable field types
const EDITABLE_TYPES = ["text", "barcode", "nfc", "number", "date", "datetime", "time", "boolean", "select", "singleSelect", "multiSelect", "multiselect", "checklist", "checklistWithNotes", "checklistwithnotes"];

export interface ChecklistItem {
  id?: string;
  label?: string;
  checked?: boolean;
  note?: string | null;
}

interface ChecklistEditorProps {
  value?: ChecklistItem[];
  options?: { value: string; label: string }[];
  onChange: (value: ChecklistItem[]) => void;
  showNotes: boolean;
  isMobile?: boolean;
}

export function normalizeChecklistValue(
  value: ChecklistItem[] | undefined,
  options: { value: string; label: string }[] = []
): ChecklistItem[] {
  const currentItems = Array.isArray(value) ? value : [];
  
  if (!options || options.length === 0) return currentItems;

  return options.map((option) => {
    const existingItem = currentItems.find((item) => String(item.id) === String(option.value));

    return {
      id: option.value,
      label: option.label,
      checked: existingItem?.checked ?? false,
      note: existingItem?.note ?? null,
    };
  });
}

function ChecklistEditor({ value = [], options = [], onChange, showNotes, isMobile = false }: ChecklistEditorProps) {
  const normalizedValue = normalizeChecklistValue(value, options);

  const handleCheckboxChange = (index: number, checked: boolean) => {
    const newValue = [...normalizedValue];
    newValue[index] = { ...newValue[index], checked };
    onChange(newValue);
  };

  const handleNoteChange = (index: number, note: string) => {
    const newValue = [...normalizedValue];
    newValue[index] = { ...newValue[index], note };
    onChange(newValue);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
      {normalizedValue.map((item, index) => {
        const label = item?.label || item?.id || `Item ${index + 1}`;
        return (
          <div key={item?.id || index} style={{ borderBottom: "1px solid #f1f3f5", paddingBottom: 8 }}>
            <Checkbox
              size={isMobile ? "md" : "sm"}
              label={label}
              checked={item?.checked || false}
              onChange={(e) => handleCheckboxChange(index, e.target.checked)}
            />
            {showNotes && item?.checked && (
              <TextInput
                placeholder="Add note..."
                value={item?.note || ""}
                onChange={(e) => handleNoteChange(index, e.target.value)}
                style={{ marginTop: 6, marginLeft: 28 }}
                size={isMobile ? "sm" : "xs"}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function getOptions(field: FormFieldConfig, selectOptions: FlexOptionsSet[]): { value: string; label: string }[] {
  if (field.optionsSource) {
    if (field.optionsSource.type === "static" && Array.isArray(field.optionsSource.options)) {
      return (field.optionsSource.options as any[]).map((opt: any) => ({
        value: String(opt.value),
        label: opt.label,
      }));
    } else if (field.optionsSource.type === "firestore") {
      const firestoreSource = field.optionsSource;
      const found = selectOptions.find((o) => o.id === firestoreSource.id);
      if (found && Array.isArray(found.options)) {
        return (found.options as any[]).map((opt: any) => ({
          value: String(opt.value),
          label: opt.label,
        }));
      }
    }
  }
  return [];
}

function computeMetadata(field: FormFieldConfig, value: any, selectOptions: FlexOptionsSet[]): any {
  let options: any[] = [];
  if (field.optionsSource) {
    if (field.optionsSource.type === "static" && Array.isArray(field.optionsSource.options)) {
      options = field.optionsSource.options;
    } else {
      const source = field.optionsSource as { id: string };
      options = selectOptions.find((o) => o.id === source.id)?.options || [];
    }
  }
  const typeStr = field.type as string;
  if ((typeStr === "multiSelect" || typeStr === "multiselect") && Array.isArray(value)) {
    return value.map((val) => {
      const opt = options.find((o) => String(o.value) === String(val));
      return opt ? (opt.metadata ? { ...opt.metadata, label: opt.label } : { label: opt.label }) : null;
    }).filter(Boolean);
  } else if ((typeStr === "select" || typeStr === "singleSelect") && value !== undefined && value !== null) {
    const opt = options.find((o) => String(o.value) === String(value));
    return opt ? (opt.metadata ? { ...opt.metadata, label: opt.label } : { label: opt.label }) : null;
  }
  return null;
}

export interface SubmissionEditFormProps {
  template: FlexFormTemplate;
  submission: TemplateSubmission & { submissionDate?: Timestamp | string | null };
  selectOptions: FlexOptionsSet[];
  onSaveData: (newData: Record<string, any>) => Promise<void> | void;
  onSaveSubmissionDate: (newDate: Timestamp) => Promise<void> | void;
  onCancel: () => void;
}

export default function SubmissionEditForm({
  template,
  submission,
  selectOptions,
  onSaveData,
  onSaveSubmissionDate,
  onCancel,
}: SubmissionEditFormProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputSize = isMobile ? "md" : "sm";
  const { control, handleSubmit, setValue } = useForm<Record<string, any>>({ mode: "onSubmit" });
  const [loading, setLoading] = useState<boolean>(false);
  const userTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "Unknown";
    }
  }, []);

  // Pre-populate form with current data
  useEffect(() => {
    if (template && submission) {
      template.fields.forEach((field) => {
        const currentValue = submission.data[field.key]?.value;
        if (currentValue !== undefined) {
          let valueToSet = currentValue;
          if (field.type === "number") {
            valueToSet = String(currentValue);
          } else if (field.type === "datetime") {
            if (currentValue?._seconds) {
              valueToSet = new Date(currentValue._seconds * 1000).toISOString();
            } else {
              valueToSet = new Date(currentValue).toISOString();
            }
          }
          setValue(field.id, valueToSet);
        }
      });
      // Set submissionDate
      if (submission.submissionDate) {
        const subDate = submission.submissionDate;
        const isTimestamp = subDate && typeof subDate === "object" && "toDate" in subDate;
        const date = isTimestamp ? (subDate as Timestamp).toDate() : new Date(subDate as string);
        setValue('submissionDate', date);
      }
    }
  }, [template, submission, setValue]);

  const onSubmit = async (values: Record<string, any>) => {
    setLoading(true);
    const newData: Record<string, any> = {};

    template.fields.forEach((field) => {
      const oldValue = submission.data[field.key]?.value;
      const newValue = values[field.id];
      const normalizedNewValue =
        field.type === "number" && newValue !== "" && newValue !== undefined && newValue !== null
          ? Number(newValue)
          : newValue;
      if (
        EDITABLE_TYPES.includes(field.type) &&
        JSON.stringify(oldValue) !== JSON.stringify(normalizedNewValue)
      ) {
        if (["select", "singleSelect", "multiSelect", "multiselect"].includes(field.type)) {
          newData[field.key] = {
            value: normalizedNewValue,
            type: field.type,
            label: field.label,
            metadata: computeMetadata(field, normalizedNewValue, selectOptions),
          };
        } else {
          newData[field.key] = {
            value: normalizedNewValue,
            type: field.type,
            label: field.label,
          };
        }
      }
    });

    // Handle submissionDate
    const newSubmissionDate = values.submissionDate;
    const oldSubmissionDate = (() => {
      const subDate = submission.submissionDate;
      const isTimestamp = subDate && typeof subDate === "object" && "toDate" in subDate;
      return isTimestamp ? (subDate as Timestamp).toDate() : new Date(subDate as string);
    })();
    let submissionDateChanged = false;
    if (newSubmissionDate && newSubmissionDate.getTime() !== oldSubmissionDate.getTime()) {
      submissionDateChanged = true;
    }

    try {
      // Save data updates
      if (Object.keys(newData).length > 0) {
        await onSaveData(newData);
      }

      // Save submissionDate update
      if (submissionDateChanged) {
        await onSaveSubmissionDate(Timestamp.fromDate(newSubmissionDate));
      }

      setLoading(false);
    } catch (error) {
      console.error("Save failed", error);
      setLoading(false);
    }
  };

  const renderField = (field: FormFieldConfig) => {
    const options = getOptions(field, selectOptions);
    const isEditable = EDITABLE_TYPES.includes(field.type);

    if (!isEditable) {
      // Read-only display
      const value = submission.data[field.key]?.value;
      return (
        <div key={field.id}>
          <Text weight="bold">{field.label}</Text>
          <FieldValueDisplay
            value={value}
            type={field.type}
            label={field.label}
            metadata={submission.data[field.key]?.metadata || null}
          />
          <Space h="sm" />
        </div>
      );
    }

    return (
      <div key={field.id}>
        <Controller
          name={field.id}
          control={control}
          rules={{}}
          render={({ field: controllerField, fieldState }) => {
            switch (field.type as string) {
              case "text":
              case "barcode":
              case "nfc":
                return (
                  <TextInput
                    size={inputSize}
                    label={field.label}
                    placeholder={field.label}
                    error={fieldState.error?.message}
                    {...controllerField}
                  />
                );
              case "number":
                return (
                  <TextInput
                    size={inputSize}
                    label={field.label}
                    placeholder={field.label}
                    type="number"
                    step="any"
                    error={fieldState.error?.message}
                    {...controllerField}
                  />
                );
              case "boolean":
                const { value: _cbValue, ...checkboxFieldProps } = controllerField;
                return (
                  <div>
                    <Checkbox
                      size={inputSize}
                      label={field.label}
                      {...checkboxFieldProps}
                      checked={controllerField.value || false}
                      onChange={(e) => controllerField.onChange(e.target.checked)}
                    />
                    {fieldState.error?.message && (
                      <Text size="xs" color="red" mt={4}>
                        {fieldState.error.message}
                      </Text>
                    )}
                  </div>
                );
              case "select":
              case "singleSelect":
                return (
                  <Select
                    size={inputSize}
                    label={field.label}
                    placeholder={field.label}
                    data={options}
                    error={fieldState.error?.message}
                    {...controllerField}
                  />
                );
              case "multiSelect":
              case "multiselect":
                return (
                  <MultiSelect
                    size={inputSize}
                    label={field.label}
                    placeholder={field.label}
                    data={options}
                    error={fieldState.error?.message}
                    {...controllerField}
                  />
                );
              case "checklist":
              case "checklistwithnotes":
              case "checklistWithNotes":
                return (
                  <div>
                    <Text size="sm" weight={500} mb={4}>{field.label}</Text>
                    <ChecklistEditor
                      value={controllerField.value || []}
                      options={options}
                      onChange={controllerField.onChange}
                      showNotes={field.type.toLowerCase() === "checklistwithnotes"}
                      isMobile={isMobile}
                    />
                  </div>
                );
              case "date":
                return (
                  <DatePicker
                    size={inputSize}
                    label={field.label}
                    placeholder={field.label}
                    error={fieldState.error?.message}
                    {...controllerField}
                    value={controllerField.value ? new Date(controllerField.value) : null}
                    onChange={(date) => controllerField.onChange(date ? date.toISOString().split('T')[0] : null)}
                  />
                );
              case "time":
                return (
                  <TimeInput
                    size={inputSize}
                    label={field.label}
                    error={fieldState.error?.message}
                    withSeconds={false}
                    value={controllerField.value ? new Date(`1970-01-01T${controllerField.value}`) : null}
                    onChange={(time: Date | null) => controllerField.onChange(time ? time.toTimeString().split(' ')[0] : null)}
                  />
                );
              case "datetime":
                return (
                  <div>
                    <DatePicker
                      size={inputSize}
                      label={field.label}
                      placeholder={field.label}
                      error={fieldState.error?.message}
                      value={new Date(controllerField.value)}
                      onChange={(date: Date | null) => {
                        const current = controllerField.value ? new Date(controllerField.value) : new Date();
                        if (date) {
                          current.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
                        }
                        controllerField.onChange(current.toISOString());
                      }}
                    />
                    <TimeInput
                      size={inputSize}
                      label=""
                      error={fieldState.error?.message}
                      withSeconds={false}
                      value={controllerField.value ? new Date(controllerField.value) : null}
                      onChange={(time: Date | null) => {
                        const current = controllerField.value ? new Date(controllerField.value) : new Date();
                        if (time) {
                          current.setHours(time.getHours(), time.getMinutes());
                        }
                        controllerField.onChange(current.toISOString());
                      }}
                    />
                    <Text size="xs" color="dimmed" style={{ marginTop: 4 }}>
                      Displayed in your local timezone ({userTimezone})
                    </Text>
                  </div>
                );
              default:
                return <></>;
            }
          }}
        />
        <Space h="sm" />
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'scroll' }}>
      <ScrollArea style={{ flex: 1 }}>
        {/* Submission Date Field */}
        {/* Card 1 */}
        <Card shadow="lg" p={16} radius="md" withBorder style={{ border: '1px solid #e0e0e0' }}>
          <Text size="md" weight={600} mb="sm">Date</Text>
          <Controller
            name="submissionDate"
            control={control}
            render={({ field: controllerField, fieldState }) => (
              <div>
                <DatePicker
                  size={inputSize}
                  placeholder="Select date"
                  value={controllerField.value}
                  onChange={(date) => {
                    const current = controllerField.value || new Date();
                    if (date) {
                      current.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
                    }
                    controllerField.onChange(new Date(current));
                  }}
                  error={fieldState.error?.message}
                />
                <TimeInput
                  size={inputSize}
                  label=""
                  value={controllerField.value || null}
                  onChange={(time: Date | null) => {
                    const current = controllerField.value || new Date();
                    if (time) {
                      current.setHours(time.getHours(), time.getMinutes());
                    }
                    controllerField.onChange(new Date(current));
                  }}
                  withSeconds={false}
                />
                <Text size="xs" color="dimmed" style={{ marginTop: 4 }}>
                  Displayed in your local timezone ({userTimezone})
                </Text>
              </div>
            )}
          />
        </Card>
        <Space h="md" />
        {/* Card 2 */}
        <Card shadow="lg" p={16} radius="md" withBorder style={{ border: '1px solid #e0e0e0' }}>
          <Text size="md" weight={600} mb="sm">Data</Text>
          {template.fields.map(renderField)}
        </Card>
      </ScrollArea>
      <Space h="md" />
      <div style={{ display: 'flex', gap: '1rem' }}>
        {loading ? <Loader /> : <>
          <Button size={inputSize} variant="default" color="gray" onClick={onCancel} style={{ flex: 1 }}>Cancel</Button>
          <Button size={inputSize} type="submit" color="cap-navy" style={{ flex: 1 }}>Save</Button>
        </>}
      </div>
    </form>
  );
}